import { HttpClient, HttpHeaders } from "@angular/common/http";
import { catchError, Observable, throwError } from "rxjs";
import { ApiResponse } from "../data/http/responses/apiresponse.response";
import { AppService } from './app.service';


export abstract class AbstractService<Req, Res> {

    protected baseUrl : string;
    protected http : HttpClient;
    
    protected appService?: AppService;

    constructor(baseUrl: string, http: HttpClient, cookies: any, appService?: AppService) {
        this.baseUrl = baseUrl;
        this.http = http;
        
        this.appService = appService;
    }

    protected getHeaders(): HttpHeaders {
        // Do not rely on cookies for the Authorization header. The global HTTP
        // interceptor provides the Authorization header from Keycloak's in-memory token.
        // Keep Content-Type here for convenience; avoid duplicating auth logic.
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });
        return headers;
    }

    // Méthode pour afficher toutes les entités
    getAll(): Observable<ApiResponse<Res[]>> {
        return this.http.get<ApiResponse<Res[]>>(
            `${this.baseUrl}`,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Méthode pour afficher une entité par son ID
    getById(id: string | number): Observable<ApiResponse<Res>> {
        return this.http.get<ApiResponse<Res>>(
            `${this.baseUrl}/${id}`,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Méthode pour créer une entité
    create(data: Req): Observable<ApiResponse<Res>> {
        return this.http.post<ApiResponse<Res>>(
            this.baseUrl,
            data,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Met à jour un élément existant
    update(id: string | number, data: Req): Observable<ApiResponse<Res>> {
        return this.http.put<ApiResponse<Res>>(
            `${this.baseUrl}/${id}`,
            data,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Supprime un élément par son ID
    delete(id: string | number): Observable<ApiResponse<any>> {
        return this.http.delete<ApiResponse<any>>(
            `${this.baseUrl}/${id}`,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Supprimer un élément en modifiant son statut (soft delete)
    softDelete(id: string | number): Observable<ApiResponse<any>> {
        return this.http.put<ApiResponse<any>>(
            `${this.baseUrl}/soft-delete/${id}`,
            {},
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Récupère tous les éléments par l'ID d'une application
    getAllByAppId(appId?: string): Observable<ApiResponse<Res[]>> {
        // Resolve appId from explicit argument, then AppService (Keycloak token), then cookies fallback
        let resolvedAppId = appId;
        if ((!resolvedAppId || resolvedAppId === 'null' || resolvedAppId === 'undefined' || resolvedAppId === 'default') && this.appService) {
            const fromToken = this.appService.getAppId();
            if (fromToken && fromToken.trim() !== '') resolvedAppId = fromToken;
        }
        if (!resolvedAppId || resolvedAppId === 'null' || resolvedAppId === 'default' || resolvedAppId === 'undefined') {
            console.warn('AbstractService.getAllByAppId called with missing or invalid appId:', appId, 'resolvedAppId:', resolvedAppId);
            return throwError(() => new Error('app_id manquant — impossible d’appeler l’API.'));
        }
        return this.http.get<ApiResponse<Res[]>>(
            `${this.baseUrl}/by-app-id/${resolvedAppId}`,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

        // Récupère tous les éléments par l'ID d'une application
    getAllByAppIdAndType( type: string, appId?: string): Observable<ApiResponse<Res[]>> {
        let resolvedAppId = appId;
        if ((!resolvedAppId || resolvedAppId === 'null' || resolvedAppId === 'undefined' || resolvedAppId === 'default') && this.appService) {
            const fromToken = this.appService.getAppId();
            if (fromToken && fromToken.trim() !== '') resolvedAppId = fromToken;
        }
        if (!resolvedAppId || resolvedAppId === 'null' || resolvedAppId === 'default' || resolvedAppId === 'undefined') {
            console.warn('AbstractService.getAllByAppIdAndType called with missing or invalid appId:', appId, 'resolvedAppId:', resolvedAppId);
            return throwError(() => new Error('app_id manquant — impossible d’appeler l’API.'));
        }
        return this.http.get<ApiResponse<Res[]>>(
            `${this.baseUrl}/by-app-id/${resolvedAppId}/${type}`,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }


    // Génère un rapport de liste
    generateListReport(id: string): Observable<ApiResponse<Res[]>> {
        return this.http.get<ApiResponse<Res[]>>(
            `${this.baseUrl}/liste-report/${id}`,
            { headers: this.getHeaders() }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // Méthode de gestion des erreurs générique
    protected handleError(error: any): Observable<never> {
        // Provide richer logs and return a meaningful Error to the UI
        try {
            console.error('Une erreur est survenue dans AbstractService:', error);
            // If it's an HttpErrorResponse, extract status and url
            const status = error?.status;
            const url = error?.url || error?.message;
            if (status === 403) {
                return throwError(() => new Error(`Accès refusé (403) pour ${url}. Vous n'avez pas les droits pour cette action.`));
            }
            if (status === 401) {
                return throwError(() => new Error(`Non autorisé (401) pour ${url}. Veuillez vous reconnecter.`));
            }
            // Generic fallback
            return throwError(() => new Error('Une erreur est survenue lors de la requête. Veuillez réessayer plus tard.'));
        } catch (e) {
            console.error('AbstractService.handleError error while formatting:', e);
            return throwError(() => new Error('Une erreur est survenue lors de la requête. Veuillez réessayer plus tard.'));
        }
    }
}